/**
 * Orchestrator Middleware
 * 
 * Intercepts user requests and routes them through the proper agent architecture:
 * 1. Main GPT-5 Orchestrator analyzes user intent
 * 2. Orchestrator calls appropriate sub-agents (email, calendar, search)
 * 3. Sub-agents execute using Graph API tools
 * 4. Orchestrator synthesizes results and explains actions to user
 * 
 * This ensures all AI assistants follow the same structured workflow.
 */

import { blink } from '@/lib/blink';
import { gpt5NanoAgent, AgentResponse } from './gpt5NanoAgent';
import { gpt5Orchestrator } from './gpt5Orchestrator';
import { unifiedAIAgentService } from './unifiedAIAgentService';
import { multiAgentOrchestrator } from './multiAgentOrchestrator';
import { apiTracer, startTraceSession, endTraceSession } from './apiTracer';
import { safeJsonParse } from '@/lib/utils';

/**
 * Orchestration step for tracking workflow execution
 */
export interface OrchestrationStep {
  id: string;
  type: 'analysis' | 'planning' | 'execution' | 'synthesis';
  agent: 'orchestrator' | 'gpt5-nano' | 'email-agent' | 'calendar-agent' | 'search-agent';
  status: 'pending' | 'running' | 'completed' | 'failed';
  input?: any;
  output?: any;
  error?: string;
  startTime?: number;
  endTime?: number;
}

/**
 * Complete orchestration result with all steps tracked
 */
export interface OrchestrationResult {
  success: boolean;
  userIntent: string;
  agentResponse: AgentResponse | null;
  executionResults: any[];
  synthesizedExplanation: string;
  steps: OrchestrationStep[];
  totalDuration: number;
}

/**
 * Orchestrator Middleware Class
 * Routes all AI requests through the proper agent hierarchy
 */
export class OrchestratorMiddleware {
  private userId: string | null = null;
  private steps: OrchestrationStep[] = [];
  private agentMode: 'answer' | 'action' = 'action'; // DEFAULT: Action mode (send emails, create events)

  /**
   * Initialize with user context
   */
  async initialize(userId: string): Promise<void> {
    this.userId = userId;
    await unifiedAIAgentService.initialize(userId);
    
    // Load user's preferred mode from preferences
    try {
      const prefs = await blink.db.userPreferences.list({
        where: { userId },
        limit: 1
      });
      
      if (prefs.length > 0 && prefs[0].preferences) {
        const prefsObj = typeof prefs[0].preferences === 'string' 
          ? safeJsonParse(prefs[0].preferences) 
          : prefs[0].preferences;
        this.agentMode = prefsObj?.agentMode || 'action';
      }
    } catch (error) {
      console.warn('Could not load agent mode preference:', error);
    }
  }

  /**
   * Set agent mode (answer vs action)
   */
  async setAgentMode(mode: 'answer' | 'action'): Promise<void> {
    this.agentMode = mode;
    console.log(`🔄 Agent mode set to: ${mode.toUpperCase()}`);
    
    // Save preference to database
    if (this.userId) {
      try {
        const prefs = await blink.db.userPreferences.list({
          where: { userId: this.userId },
          limit: 1
        });
        
        if (prefs.length > 0) {
          const existingPrefs = typeof prefs[0].preferences === 'string'
            ? safeJsonParse(prefs[0].preferences)
            : prefs[0].preferences || {};
          
          await blink.db.userPreferences.update(prefs[0].id, {
            preferences: JSON.stringify({
              ...existingPrefs,
              agentMode: mode
            }),
            updatedAt: new Date().toISOString()
          });
        } else {
          await blink.db.userPreferences.create({
            id: `pref_${Date.now()}`,
            userId: this.userId,
            preferences: JSON.stringify({ agentMode: mode }),
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          });
        }
        
        console.log(`✅ Agent mode preference saved: ${mode}`);
      } catch (error) {
        console.warn('Could not save agent mode preference:', error);
      }
    }
  }

  /**
   * Get current agent mode
   */
  getAgentMode(): 'answer' | 'action' {
    return this.agentMode;
  }

  /**
   * Main orchestration entry point
   * Processes user request through the complete agent workflow
   * 
   * Automatically detects complex requests and routes to multi-agent orchestrator
   */
  async processRequest(
    userMessage: string,
    onStep?: (step: OrchestrationStep) => void
  ): Promise<OrchestrationResult> {
    if (!this.userId) {
      throw new Error('Orchestrator not initialized. Call initialize() first.');
    }

    const startTime = Date.now();
    this.steps = [];

    // Start API tracing session
    const traceSessionId = startTraceSession(this.userId, userMessage);
    console.log(`🔍 Started trace session: ${traceSessionId}`);

    try {
      // Detect if request requires multi-agent orchestration
      const requiresMultiAgent = await this.detectComplexRequest(userMessage);

      if (requiresMultiAgent) {
        console.log('🔀 Routing to Multi-Agent Orchestrator for complex request');
        return await this.executeMultiAgentWorkflow(userMessage, onStep);
      }

      // Standard single-agent workflow for simple requests
      // Step 1: Main GPT-5 Orchestrator analyzes user intent
      const analysisStep = this.createStep('analysis', 'orchestrator', {
        userMessage
      });
      onStep?.(analysisStep);

      const intent = await this.analyzeUserIntent(userMessage);
      this.completeStep(analysisStep, { intent });
      onStep?.(analysisStep);

      // Step 2: GPT-5 Nano Agent creates structured action plan
      const planningStep = this.createStep('planning', 'gpt5-nano', {
        intent
      });
      onStep?.(planningStep);

      const agentResponse = await gpt5NanoAgent.processUserIntent(userMessage);
      this.completeStep(planningStep, { agentResponse });
      onStep?.(planningStep);

      // Validate agent response
      if (!agentResponse.validation.isValid) {
        return {
          success: false,
          userIntent: userMessage,
          agentResponse,
          executionResults: [],
          synthesizedExplanation: this.buildErrorExplanation(agentResponse),
          steps: this.steps,
          totalDuration: Date.now() - startTime
        };
      }

      // Step 3: Execute via appropriate sub-agents
      const executionStep = this.createStep('execution', this.getAgentForAction(agentResponse.action.type), {
        action: agentResponse.action,
        mode: this.agentMode
      });
      onStep?.(executionStep);

      // Check if action is allowed in current mode
      const { isActionAllowed } = await import('@/types/agentMode');
      if (!isActionAllowed(agentResponse.action.type, this.agentMode)) {
        const modeRestrictionError = `⚠️ **Agent Mode Restriction**\n\n**Action Required:** "${agentResponse.action.type}" needs **Action Mode** enabled.\n\n**🔐 Your Current Mode:** Answer Mode (Read-Only)\n✅ Search and retrieve emails\n✅ Analyze contacts and relationships\n✅ Generate summaries and insights\n❌ Send emails via YOUR Gmail/Outlook account\n❌ Create calendar events\n❌ Modify data\n\n**📧 To send emails using YOUR Google or Microsoft account:**\n1. ✓ Click the "Action Mode" toggle button at the top\n2. ✓ Verify your Google/Outlook account is connected (check Settings)\n3. ✓ Try your command again\n\n💡 **Important:** All emails are sent directly through **Google Gmail API** or **Microsoft Graph API** using YOUR email address - never through third-party services or Blink SDK.`;
        
        return {
          success: false,
          userIntent: userMessage,
          agentResponse,
          executionResults: [],
          synthesizedExplanation: modeRestrictionError,
          steps: this.steps,
          totalDuration: Date.now() - startTime
        };
      }

      const executionResults = await this.executeViaSubAgents(agentResponse);
      this.completeStep(executionStep, { results: executionResults });
      onStep?.(executionStep);

      // Step 4: Synthesize explanation of what was done
      const synthesisStep = this.createStep('synthesis', 'orchestrator', {
        agentResponse,
        executionResults
      });
      onStep?.(synthesisStep);

      const explanation = await this.synthesizeExplanation(
        userMessage,
        agentResponse,
        executionResults
      );
      this.completeStep(synthesisStep, { explanation });
      onStep?.(synthesisStep);

      // End trace session
      const traceSession = endTraceSession(traceSessionId);
      console.log(`✅ Trace session completed: ${traceSessionId}`);
      console.log(`   API Calls: ${traceSession?.summary.totalCalls || 0}`);
      console.log(`   Success Rate: ${traceSession ? (traceSession.summary.successfulCalls / traceSession.summary.totalCalls * 100).toFixed(1) : 0}%`);

      return {
        success: true,
        userIntent: userMessage,
        agentResponse,
        executionResults,
        synthesizedExplanation: explanation,
        steps: this.steps,
        totalDuration: Date.now() - startTime
      };

    } catch (error) {
      console.error('Orchestration failed:', error);
      
      const failedStep = this.steps[this.steps.length - 1];
      if (failedStep) {
        failedStep.status = 'failed';
        failedStep.error = error instanceof Error ? error.message : 'Unknown error';
        failedStep.endTime = Date.now();
        onStep?.(failedStep);
      }

      // End trace session on error
      endTraceSession(traceSessionId);
      console.log(`❌ Trace session ended with error: ${traceSessionId}`);

      return {
        success: false,
        userIntent: userMessage,
        agentResponse: null,
        executionResults: [],
        synthesizedExplanation: `Error: ${error instanceof Error ? error.message : 'Failed to process request'}`,
        steps: this.steps,
        totalDuration: Date.now() - startTime
      };
    }
  }

  /**
   * Detect if request requires multi-agent orchestration
   */
  private async detectComplexRequest(userMessage: string): Promise<boolean> {
    // Keywords that indicate multi-agent complexity
    const multiAgentKeywords = [
      /find.*and.*search/i,
      /\d+\s+(emails?|meetings?|contacts?|topics?)/i,
      /multiple|several|various/i,
      /compare.*with/i,
      /analyze.*across/i,
      /search.*by.*and/i,
      /all.*from.*and/i
    ];

    // Check for complexity indicators
    const hasMultipleTargets = multiAgentKeywords.some(pattern => 
      pattern.test(userMessage)
    );

    // Check for numeric counts (e.g., "3 emails", "2 meetings")
    const hasNumericCounts = /\d+\s+(emails?|meetings?|contacts?|events?|topics?)/i.test(userMessage);

    // Check for multiple operations with "and"
    const hasMultipleOperations = userMessage.toLowerCase().split(' and ').length > 2;

    return hasMultipleTargets || hasNumericCounts || hasMultipleOperations;
  }

  /**
   * Execute multi-agent workflow for complex requests
   */
  private async executeMultiAgentWorkflow(
    userMessage: string,
    onStep?: (step: OrchestrationStep) => void
  ): Promise<OrchestrationResult> {
    const startTime = Date.now();

    try {
      // Execute multi-agent orchestration
      const result = await multiAgentOrchestrator.orchestrate(
        userMessage,
        (update) => {
          // Convert multi-agent updates to orchestration steps
          const step = this.createStep(
            update.phase === 'parsing' ? 'analysis' : 
            update.phase === 'provisioning' ? 'planning' : 
            update.phase === 'executing' ? 'execution' : 'synthesis',
            'orchestrator',
            update.data
          );
          this.completeStep(step, update.data);
          onStep?.(step);
        }
      );

      // Convert multi-agent result to orchestration result
      return {
        success: result.success,
        userIntent: userMessage,
        agentResponse: null, // Multi-agent doesn't use single agent response
        executionResults: result.raw_agent_results,
        synthesizedExplanation: this.formatMultiAgentResults(result),
        steps: this.steps,
        totalDuration: Date.now() - startTime
      };
    } catch (error) {
      console.error('Multi-agent orchestration failed:', error);
      
      return {
        success: false,
        userIntent: userMessage,
        agentResponse: null,
        executionResults: [],
        synthesizedExplanation: `Multi-agent orchestration failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        steps: this.steps,
        totalDuration: Date.now() - startTime
      };
    }
  }

  /**
   * Format multi-agent results into user-friendly explanation
   */
  private formatMultiAgentResults(result: any): string {
    let explanation = `# Multi-Agent Orchestration Results\n\n`;
    
    explanation += `## Workflow Summary\n`;
    explanation += `- **Total Agents Deployed**: ${result.workflow_summary.total_agents_deployed}\n`;
    explanation += `- **Execution Time**: ${(result.workflow_summary.execution_time_ms / 1000).toFixed(2)}s\n`;
    explanation += `- **Success Rate**: ${(result.workflow_summary.success_rate * 100).toFixed(1)}%\n\n`;

    explanation += `## Results by Category\n`;
    for (const [category, data] of Object.entries(result.results_by_category)) {
      const categoryData = data as any;
      explanation += `### ${category.charAt(0).toUpperCase() + category.slice(1)}\n`;
      explanation += `- Agents deployed: ${categoryData.agents_deployed}\n`;
      explanation += `- Results found: ${categoryData.results.length}\n\n`;
    }

    if (result.cross_correlation_insights.length > 0) {
      explanation += `## Key Insights\n`;
      result.cross_correlation_insights.forEach((insight: string, idx: number) => {
        explanation += `${idx + 1}. ${insight}\n`;
      });
      explanation += `\n`;
    }

    if (result.recommended_actions.length > 0) {
      explanation += `## Recommended Actions\n`;
      result.recommended_actions.forEach((action: string, idx: number) => {
        explanation += `${idx + 1}. ${action}\n`;
      });
    }

    return explanation;
  }

  /**
   * Step 1: Analyze user intent using GPT-5 orchestrator
   */
  private async analyzeUserIntent(userMessage: string): Promise<string> {
    const { text } = await blink.ai.generateText({
      prompt: `You are the main GPT-5 orchestrator. Analyze this user request and identify the core intent in 1-2 sentences:

User Request: "${userMessage}"

What is the user trying to accomplish? Be specific and actionable.`,
      maxTokens: 200
    });

    return text.trim();
  }

  /**
   * Step 3: Execute action via appropriate sub-agents
   */
  private async executeViaSubAgents(agentResponse: AgentResponse): Promise<any[]> {
    const results: any[] = [];

    try {
      // Execute the action through the GPT-5 Nano agent
      // This ensures all Graph API calls go through the proper agent structure
      const result = await gpt5NanoAgent.executeAction(agentResponse.action);
      results.push(result);

      return results;
    } catch (error) {
      console.error('Sub-agent execution failed:', error);
      throw error;
    }
  }

  /**
   * Step 4: Synthesize explanation using GPT-5
   */
  private async synthesizeExplanation(
    userMessage: string,
    agentResponse: AgentResponse,
    executionResults: any[]
  ): Promise<string> {
    const prompt = `You are the GPT-5 orchestrator synthesizing the results of a multi-agent workflow.

**User Request:** "${userMessage}"

**Agent Analysis:**
${agentResponse.thinking}

**Action Taken:** ${agentResponse.action.type}
**Confidence:** ${agentResponse.action.confidence}%
**Reasoning:** ${agentResponse.reasoning}

**Execution Results:**
${JSON.stringify(executionResults, null, 2)}

**Your Task:**
Explain to the user in a clear, friendly way:
1. What you understood they wanted
2. What specific actions you took (mention Graph API operations if relevant)
3. The result of those actions
4. Any important details they should know

Keep it conversational and helpful. Use markdown formatting.`;

    const { text } = await blink.ai.generateText({
      prompt,
      maxTokens: 600
    });

    return text.trim();
  }

  /**
   * Build error explanation from validation failures
   */
  private buildErrorExplanation(agentResponse: AgentResponse): string {
    let explanation = '❌ **Unable to process your request**\n\n';
    
    if (agentResponse.validation.errors.length > 0) {
      explanation += '**Issues found:**\n';
      agentResponse.validation.errors.forEach(error => {
        explanation += `- ${error}\n`;
      });
    }

    if (agentResponse.validation.warnings.length > 0) {
      explanation += '\n**Warnings:**\n';
      agentResponse.validation.warnings.forEach(warning => {
        explanation += `- ${warning}\n`;
      });
    }

    if (agentResponse.alternatives && agentResponse.alternatives.length > 0) {
      explanation += '\n**Suggestions:**\n';
      agentResponse.alternatives.forEach(alt => {
        explanation += `- Try: ${alt.type} - ${alt.reason}\n`;
      });
    }

    return explanation;
  }

  /**
   * Helper: Create orchestration step
   */
  private createStep(
    type: OrchestrationStep['type'],
    agent: OrchestrationStep['agent'],
    input?: any
  ): OrchestrationStep {
    const step: OrchestrationStep = {
      id: `step_${Date.now()}_${Math.random().toString(36).substring(7)}`,
      type,
      agent,
      status: 'running',
      input,
      startTime: Date.now()
    };

    this.steps.push(step);
    return step;
  }

  /**
   * Helper: Complete orchestration step
   */
  private completeStep(step: OrchestrationStep, output: any): void {
    step.status = 'completed';
    step.output = output;
    step.endTime = Date.now();
  }

  /**
   * Helper: Determine which agent handles an action
   */
  private getAgentForAction(actionType: string): OrchestrationStep['agent'] {
    if (actionType.startsWith('email_')) return 'email-agent';
    if (actionType.startsWith('calendar_')) return 'calendar-agent';
    if (actionType.includes('search')) return 'search-agent';
    return 'gpt5-nano';
  }

  /**
   * Get current orchestration steps
   */
  getSteps(): OrchestrationStep[] {
    return this.steps;
  }
}

/**
 * Singleton instance
 */
export const orchestratorMiddleware = new OrchestratorMiddleware();

/**
 * Convenience function to process request with automatic initialization
 */
export async function processWithOrchestrator(
  userMessage: string,
  userId: string,
  onStep?: (step: OrchestrationStep) => void
): Promise<OrchestrationResult> {
  await orchestratorMiddleware.initialize(userId);
  return await orchestratorMiddleware.processRequest(userMessage, onStep);
}
